import React from 'react';

/* ──────────────────────────────────────────────────────────────────
 * BillingFooterActions — Region 3 footer card
 *
 * Card-styled footer pinned at the bottom of the scrollable detail
 * region. Left = status hint. Right = buttons that vary by status.
 *
 * Clicking **Finalize** opens the CompareModal (prior-period diff)
 * which contains the actual commit button. The design's inline-
 * confirm pattern was replaced per user preference for the prior-
 * compare safety check — Duncan likes seeing the line-level Δ vs
 * the previous finalized period before committing.
 * ────────────────────────────────────────────────────────────────── */

const fmtDateMD = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const LockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const Spinner = ({ size = 13, color = '#fff' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: 99,
    border: `2px solid color-mix(in oklch, ${color} 30%, transparent)`,
    borderTopColor: color,
    animation: 'spin .8s linear infinite',
    flexShrink: 0,
  }} />
);

const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 36, padding: '0 14px', borderRadius: 8,
  fontSize: 14.5, fontWeight: 500, color: 'var(--text-2)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  cursor: 'pointer',
};
const dangerGhostBtn = {
  ...ghostBtn,
  color: 'var(--danger)',
  border: '1px solid color-mix(in oklch, var(--danger) 35%, transparent)',
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 36, padding: '0 16px', borderRadius: 8,
  fontSize: 14.5, fontWeight: 500, color: '#fff',
  background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
  boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
  cursor: 'pointer',
};

const BillingFooterActions = ({
  period,
  onDelete,
  onFinalize,
  onExportCSV,
  onRevise,
  finalizing,
  deleting,
  revising,
}) => {
  if (!period) return null;
  const isDraft = period.status === 'Draft';

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      padding: '12px 18px',
      minHeight: 60,
      display: 'flex', alignItems: 'center', gap: 12,
      flexWrap: 'wrap',
    }}>
      {/* Left — status hint */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        {isDraft ? (
          <>
            <span style={{
              width: 8, height: 8, borderRadius: 99,
              background: 'var(--text-4)',
              boxShadow: '0 0 0 3px var(--bg-hover)',
              flexShrink: 0,
            }} />
            <span style={{ fontSize: 14, color: 'var(--text-2)', fontWeight: 500 }}>
              Draft pay application
            </span>
            {period.updated_at && (
              <span className="mono" style={{ fontSize: 13, color: 'var(--text-4)' }}>
                · Last edited {fmtDateMD(period.updated_at?.slice(0, 10))}
              </span>
            )}
          </>
        ) : (
          <>
            <span style={{ color: 'var(--text-3)', flexShrink: 0 }}><LockIcon /></span>
            <span style={{ fontSize: 14, color: 'var(--text-2)' }}>
              Finalized on{' '}
              <span className="mono" style={{ fontWeight: 600, color: 'var(--text-1)' }}>
                {fmtDateMD(period.finalized_at?.slice(0, 10) || period.invoice_date)}
              </span>
              <span style={{ color: 'var(--text-4)', marginLeft: 6 }}>· Locked</span>
            </span>
          </>
        )}
      </div>

      {/* Right — actions */}
      {isDraft ? (
        <>
          <button
            type="button"
            onClick={onDelete}
            disabled={finalizing || deleting}
            style={{
              ...dangerGhostBtn,
              opacity: (finalizing && !deleting) ? 0.45 : 1,
              cursor: deleting ? 'wait' : (finalizing ? 'not-allowed' : 'pointer'),
            }}
            onMouseEnter={(e) => { if (!finalizing && !deleting) e.currentTarget.style.background = 'var(--danger-soft)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-surface)'; }}
          >
            {deleting ? <><Spinner size={12} color="var(--danger)" /> Deleting…</> : 'Delete'}
          </button>
          {/* Finalize — opens the CompareModal with the prior-period
              diff and a "Finalize draft" commit button inside. */}
          <button
            type="button"
            onClick={() => !finalizing && !deleting && onFinalize()}
            disabled={finalizing || deleting}
            style={{
              ...primaryBtn,
              opacity: (finalizing || deleting) ? 0.85 : 1,
              cursor: finalizing ? 'wait' : (deleting ? 'not-allowed' : 'pointer'),
            }}
            onMouseEnter={(e) => { if (!finalizing && !deleting) e.currentTarget.style.background = 'var(--accent-hover)'; }}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--accent)'}
          >
            {finalizing ? <><Spinner /> Finalizing…</> : 'Finalize'}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onExportCSV}
            style={ghostBtn}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
          >Export CSV</button>
          {/* Retry CE Sync button removed alongside the sidebar +
              G702 chips — same unreliability story. A dedicated CE
              sync admin tab is planned. */}
          <button
            type="button"
            onClick={onRevise}
            disabled={revising}
            style={{
              ...primaryBtn,
              opacity: revising ? 0.85 : 1,
              cursor: revising ? 'wait' : 'pointer',
            }}
          >
            {revising ? <><Spinner /> Revising…</> : 'Revise'}
          </button>
        </>
      )}
    </div>
  );
};

export default BillingFooterActions;
